import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, CheckCircle2, Circle, Clock } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_BADGE = {
  low: "bg-zinc-100 text-zinc-700 border-zinc-200",
  normal: "bg-blue-50 text-blue-800 border-blue-200",
  high: "bg-amber-50 text-amber-800 border-amber-200",
  urgent: "bg-rose-50 text-rose-800 border-rose-200",
};

const STATUS_ICON = {
  open: Circle,
  in_progress: Clock,
  done: CheckCircle2,
};

export default function MyTasks() {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState({ open: false, edit: null });
  const [filter, setFilter] = useState("all"); // all|open|in_progress|done

  const tasksQ = useQuery({
    queryKey: ["my-tasks", filter],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (filter !== "all") p.set("status", filter);
      return (await api.get(`/my-tasks${p.toString() ? "?" + p.toString() : ""}`)).data;
    },
  });

  const saveMut = useMutation({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...body } = payload;
        return (await api.patch(`/my-tasks/${id}`, body)).data;
      }
      return (await api.post("/my-tasks", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
      qc.invalidateQueries({ queryKey: ["my-work"] });
      toast.success("Saved");
      setDialog({ open: false, edit: null });
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/my-tasks/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-tasks"] });
      qc.invalidateQueries({ queryKey: ["my-work"] });
      toast.success("Deleted");
    },
  });

  const toggleStatus = (t) => {
    const next = t.status === "done" ? "open" : "done";
    saveMut.mutate({ id: t.id, status: next });
  };

  const tasks = tasksQ.data || [];

  return (
    <div className="space-y-6" data-testid="my-tasks-page">
      <PageHeader
        title="My Tasks"
        subtitle="Your personal to-do list. Private to you — nobody else, including your manager or MD, can see these."
        actions={
          <Button onClick={() => setDialog({ open: true, edit: null })} data-testid="new-task-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New task
          </Button>
        }
      />

      <div className="flex items-center gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="h-9 w-44" data-testid="task-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="done">Done</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-zinc-500 font-mono">{tasks.length} task{tasks.length === 1 ? "" : "s"}</span>
      </div>

      <div className="space-y-2">
        {tasks.map((t) => {
          const StatusIcon = STATUS_ICON[t.status] || Circle;
          return (
            <Card key={t.id} className={`p-4 ${t.status === "done" ? "bg-zinc-50/70" : ""}`} data-testid={`task-row-${t.id}`}>
              <div className="flex items-start gap-3">
                <button
                  onClick={() => toggleStatus(t)}
                  className="mt-0.5"
                  aria-label={t.status === "done" ? "Reopen" : "Mark done"}
                  data-testid={`task-toggle-${t.id}`}
                >
                  <StatusIcon className={`w-4 h-4 ${t.status === "done" ? "text-emerald-600" : "text-zinc-400"}`} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${t.status === "done" ? "text-zinc-500 line-through" : "text-zinc-900"}`}>
                    {t.title}
                  </div>
                  {t.description && <div className="text-xs text-zinc-500 mt-0.5 whitespace-pre-wrap">{t.description}</div>}
                  <div className="flex items-center gap-2 mt-1.5">
                    <Badge variant="outline" className={`text-[10px] ${PRIORITY_BADGE[t.priority] || ""}`}>{t.priority}</Badge>
                    {t.due_date && (
                      <span className="text-[11px] text-zinc-500 font-mono">
                        Due · {t.due_date}{t.due_time ? ` · ${t.due_time}` : ""}
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-400">·</span>
                    <button className="text-[11px] text-zinc-500 hover:text-zinc-800 underline" onClick={() => setDialog({ open: true, edit: t })} data-testid={`edit-task-${t.id}`}>
                      Edit
                    </button>
                  </div>
                </div>
                <button
                  onClick={() => deleteMut.mutate(t.id)}
                  className="p-1.5 rounded hover:bg-rose-50 text-zinc-400 hover:text-rose-600"
                  aria-label="Delete"
                  data-testid={`delete-task-${t.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </Card>
          );
        })}
        {!tasks.length && (
          <div className="text-center py-14 text-sm text-zinc-500">
            No {filter === "all" ? "" : filter.replace("_", " ")} tasks. Click "New task" to add one.
          </div>
        )}
      </div>

      <TaskDialog
        dialog={dialog}
        onClose={() => setDialog({ open: false, edit: null })}
        onSave={(v) => saveMut.mutate(v)}
      />
    </div>
  );
}

function TaskDialog({ dialog, onClose, onSave }) {
  const edit = dialog.edit;
  const [form, setForm] = useState(() => edit || {
    title: "", description: "", due_date: "", due_time: "", priority: "normal", status: "open",
  });
  // Reset form when opening dialog for different task
  const key = edit?.id || "new";
  return (
    <Dialog key={key} open={dialog.open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{edit ? "Edit task" : "New personal task"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input defaultValue={edit?.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" />
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Textarea defaultValue={edit?.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due date</Label>
              <Input type="date" defaultValue={edit?.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
            <div>
              <Label>Due time</Label>
              <Input type="time" defaultValue={edit?.due_time || ""} onChange={(e) => setForm({ ...form, due_time: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Priority</Label>
              <Select defaultValue={edit?.priority || "normal"} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select defaultValue={edit?.status || "open"} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="task-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(edit ? { id: edit.id, ...form } : form)} data-testid="task-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
